import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "@/lib/api";
import { DarkHero, NewsCard, NewsRow, AthleteCard, FixtureRow, VideoCard, SectionHead, Loader, Empty } from "@/components/Blocks";
import { Newsletter } from "@/components/Footer";
import { StandingsTable } from "@/components/StandingsTable";
import { useT } from "@/lib/i18n";

const TABS = ["News", "Matches", "Standings", "Athletes", "Teams", "Competitions", "Videos"];

export default function SportHub({ slug: fixed }) {
  const params = useParams();
  const slug = fixed || params.slug;
  const { t } = useT();
  const [tab, setTab] = useState("News");
  const { data, isLoading, error } = useApi("sport", `/sport/${slug}`);
  if (isLoading) return <Loader />;
  if (error || !data) return <div className="container-x py-20"><Empty text="Sport not found." /></div>;
  const { sport, news, athletes, teams, competitions, upcoming, results, videos, standings = [] } = data;
  const [lead, ...rest] = news;
  const tabs = TABS.filter((x) => x !== "Standings" || standings.length);

  return (
    <div data-testid={`sport-page-${slug}`}>
      <DarkHero title={sport.name} subtitle={sport.tagline} image={sport.image} />
      <div className="border-b border-slate-200 bg-white">
        <div className="container-x flex gap-6 overflow-x-auto no-scrollbar">
          {tabs.map((x) => (
            <button key={x} onClick={() => setTab(x)} data-testid={`sport-tab-${x.toLowerCase()}`}
              className={`whitespace-nowrap border-b-[3px] py-4 text-sm font-bold transition-colors ${tab === x ? "border-[#047857] text-[#047857]" : "border-transparent text-slate-600 hover:text-slate-900"}`}>{t(x)}</button>
          ))}
        </div>
      </div>

      <div className="container-x py-10">
        {tab === "News" && (
          <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
            <div>
              {lead ? <NewsCard item={lead} big /> : <Empty />}
              <div className="mt-10"><SectionHead title="Upcoming Matches" to="/live-scores" id="hub-upcoming" /><div className="space-y-2">{upcoming.length ? upcoming.map((f) => <FixtureRow key={f.id} f={f} />) : <Empty text={`No upcoming ${sport.name.toLowerCase()} fixtures in our online sources yet.`} />}</div></div>
              <div className="mt-10"><SectionHead title="Featured Players" id="hub-players" /><div className="grid grid-cols-2 gap-4 sm:grid-cols-4">{athletes.filter((a) => a.image).slice(0, 4).map((a) => <AthleteCard key={a.slug} a={a} showSport={false} />)}</div></div>
              {standings.length > 0 && <div className="mt-10"><SectionHead title="Standings" id="hub-standings" /><StandingsTable s={standings[0]} /></div>}
            </div>
            <aside>
              <h3 className="font-display text-2xl font-extrabold text-slate-900">{t("Latest News")}</h3>
              <div className="mt-2">{rest.slice(0, 8).map((n) => <NewsRow key={n.slug} item={n} />)}</div>
              {videos.length > 0 && <><h3 className="mt-8 font-display text-2xl font-extrabold text-slate-900">{t("Videos")}</h3><div className="mt-3 space-y-4">{videos.slice(0, 2).map((v) => <VideoCard key={v.video_id} v={v} />)}</div></>}
            </aside>
          </div>
        )}
        {tab === "Standings" && <div className="grid gap-8 lg:grid-cols-2" data-testid="hub-standings-grid">{standings.map((s) => <StandingsTable key={s.slug} s={s} />)}</div>}
        {tab === "Matches" && (
          <div className="grid gap-10 lg:grid-cols-2">
            <div><SectionHead title="Results" id="hub-results" /><div className="space-y-2">{results.length ? results.map((f) => <FixtureRow key={f.id} f={f} />) : <Empty />}</div></div>
            <div><SectionHead title="Fixtures" id="hub-fixtures" /><div className="space-y-2">{upcoming.length ? upcoming.map((f) => <FixtureRow key={f.id} f={f} />) : <Empty />}</div></div>
          </div>
        )}
        {tab === "Athletes" && <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="hub-athletes-grid">{athletes.map((a) => <AthleteCard key={a.slug} a={a} showSport={false} />)}</div>}
        {tab === "Teams" && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{teams.length ? teams.map((t) => <TeamCard key={t.slug} t={t} />) : <Empty />}</div>}
        {tab === "Competitions" && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{competitions.length ? competitions.map((c) => <CompetitionCard key={c.slug} c={c} />) : <Empty />}</div>}
        {tab === "Videos" && <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{videos.length ? videos.map((v) => <VideoCard key={v.video_id} v={v} />) : <Empty text="No video feed connected for this sport yet." />}</div>}
      </div>
      <Newsletter />
    </div>
  );
}

export const TeamCard = ({ t }) => (
  <Link to={`/teams/${t.slug}`} data-testid={`team-card-${t.slug}`} className="card-hover flex items-center gap-4 rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
    <div className="grid h-16 w-16 flex-none place-items-center overflow-hidden rounded-lg bg-slate-50">{t.badge ? <img src={t.badge} alt="" className="h-14 w-14 object-contain" /> : <span className="font-display text-2xl font-black text-slate-300">{t.name[0]}</span>}</div>
    <div className="min-w-0"><h4 className="truncate font-bold text-slate-900">{t.name}</h4><p className="text-xs text-slate-500">{t.kind} · {t.nickname}</p></div>
  </Link>
);

export const CompetitionCard = ({ c }) => (
  <Link to={`/competitions/${c.slug}`} data-testid={`competition-card-${c.slug}`} className="card-hover flex items-center gap-4 rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
    <div className="grid h-16 w-16 flex-none place-items-center overflow-hidden rounded-lg bg-slate-50">{c.logo ? <img src={c.logo} alt="" className="h-14 w-14 object-contain" /> : <span className="font-display text-2xl font-black text-slate-300">{c.name[0]}</span>}</div>
    <div className="min-w-0"><h4 className="truncate font-bold text-slate-900">{c.name}</h4><p className="text-xs text-slate-500 line-clamp-2">{c.summary}</p></div>
  </Link>
);
